import { z } from 'zod';

import { DOCUMENT_STATUSES } from '@/lib/document-status';

import { STAGE_KINDS } from '@/lib/stage-kind';

// Contratos del Panel del QMS (so=430) sobre los endpoints de so=410 (qms-api
// `modules/dashboard/views.ts`), validados con Zod: una respuesta con forma inesperada
// cae al error de su tarjeta, nunca a una excepción en el render. Ids como texto,
// fechas calendario `YYYY-MM-DD` en el día de negocio de Costa Rica.

const count = z.number().int().nonnegative();
const pct = z.number().min(0).max(100);
const envelope = <T extends z.ZodType>(data: T) => z.object({ success: z.literal(true), data });

const person = z.object({ id: z.string(), name: z.string() });

export const summaryResponseSchema = envelope(
  z.object({
    asOf: z.string(),
    controlled: z.object({ total: count, processCount: count }),
    current: z.object({ total: count, pct: pct.nullable(), upToDate: count }),
    pendingSignature: z.object({ total: count, dueToday: count, overdue: count }),
    // G4: documentos con la capacitación abierta; un API anterior sin el campo, en cero.
    inTraining: z.object({ total: count, overdue: count }).default({ total: 0, overdue: 0 }),
    overdue: z.object({
      documents: count,
      reviewOverdue: count,
      stageOverdue: count,
      escalated: count,
    }),
  }),
);
export type Summary = z.infer<typeof summaryResponseSchema>['data'];

const deadlineItemSchema = z.object({
  kind: z.enum(['stage', 'review']),
  entry: z.object({
    id: z.string(),
    code: z.string(),
    title: z.string().nullable(),
    status: z.enum(DOCUMENT_STATUSES),
  }),
  stage: z.enum(STAGE_KINDS).nullable(),
  dueDate: z.string(),
  dueAt: z.string().nullable(),
  daysLeft: z.number().int(),
  overdue: z.boolean(),
  responsibles: z.array(person),
  assignedByPosition: z.boolean(),
  escalatedTo: z.array(person),
});
export type DeadlineItem = z.infer<typeof deadlineItemSchema>;
export type Person = z.infer<typeof person>;

export const deadlinesResponseSchema = envelope(
  z.object({
    asOf: z.string(),
    window: z.number().int().positive(),
    total: count,
    items: z.array(deadlineItemSchema),
  }),
);
export type Deadlines = z.infer<typeof deadlinesResponseSchema>['data'];

const pendingItemSchema = z.object({
  flowInstanceId: z.string(),
  entry: z.object({ id: z.string(), code: z.string(), title: z.string().nullable() }),
  kind: z.string(),
  targetVersion: z.number().int(),
  dueDate: z.string().nullable(),
  daysLeft: z.number().int().nullable(),
  overdue: z.boolean(),
  /** G2-8: firmas de la ronda, quién falta y si le toca al lector. */
  signers: z.object({
    signed: z.array(z.object({ name: z.string(), at: z.string() })),
    pending: z.array(z.string()),
    mine: z.boolean(),
  }),
});
export type PendingItem = z.infer<typeof pendingItemSchema>;

export const pendingSignaturesResponseSchema = envelope(
  z.object({
    asOf: z.string(),
    total: count,
    dueToday: count,
    overdue: count,
    items: z.array(pendingItemSchema),
  }),
);
export type PendingSignatures = z.infer<typeof pendingSignaturesResponseSchema>['data'];

export const normComplianceResponseSchema = envelope(
  z.object({
    asOf: z.string(),
    norms: z.array(
      z.object({
        normCode: z.string(),
        clauses: count,
        coveredClauses: count,
        uncoveredClauses: count,
        coveragePct: pct,
        mappedDocuments: count,
      }),
    ),
  }),
);
export type NormCompliance = z.infer<typeof normComplianceResponseSchema>['data'];
export type NormCoverage = NormCompliance['norms'][number];
